import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4'

const COOKIE_CONSENT_KEY = "cookieAccepted"

export type AnalyticsConsent = "accepted" | "rejected" | "unknown"

export function getAnalyticsConsent(): AnalyticsConsent {
  try {
    const value = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (value === "true") return "accepted"
    if (value === "false") return "rejected"
  } catch {
  }
  return "unknown"
}

export function getGoogleAnalyticsIds(
  googleAnalyticsIds: { key: string; type: string }[],
): string[] {
  return googleAnalyticsIds.map((id) => id.key).filter(Boolean)
}

export function disableGoogleAnalytics(googleAnalyticsIds: string[]) {
  for (const id of googleAnalyticsIds) {
    (window as any)[`ga-disable-${id}`] = true;
  }
}

export function enableGoogleAnalytics(googleAnalyticsIds: string[]) {
  for (const id of googleAnalyticsIds) {
    delete (window as any)[`ga-disable-${id}`];
  }
}

export function clearGoogleAnalyticsCookies() {
  if (typeof document === "undefined") return

  const cookies = document.cookie.split(";")
  for (const cookie of cookies) {
    const name = cookie.split("=")[0]?.trim()
    if (!name) continue
    if (
      name === "_ga" ||
      name === "_gid" ||
      name === "_gat" ||
      name.startsWith("_ga_") ||
      name.startsWith("_gat_")
    ) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`
    }
  }
}

export function rejectAnalytics(googleAnalyticsIds: string[]) {
  disableGoogleAnalytics(googleAnalyticsIds)
  clearGoogleAnalyticsCookies()
}

export function acceptAnalytics(googleAnalyticsIds: string[]) {
  enableGoogleAnalytics(googleAnalyticsIds)
  if (googleAnalyticsIds.length > 0 && !ReactGA.isInitialized) {
    ReactGA.initialize(googleAnalyticsIds.map((trackingId) => ({ trackingId })))
  }
}

export function trackClickEvent(eventCategory: string, eventLabel: string) {
  if (getAnalyticsConsent() !== "accepted") return
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'click,auxclick,contextmenu', {
      event_category: eventCategory,
      event_label: eventLabel,
    });
  }
}

export interface AnalyticsProps {
  googleAnalyticsIds: { key: string, type: string }[]
}

export default function Analytics({ googleAnalyticsIds }: AnalyticsProps) {
  const location = useLocation();
  const trackingIdsKey = googleAnalyticsIds.map((id) => id.key).join(",")

  useEffect(() => {
    const trackingIds = getGoogleAnalyticsIds(googleAnalyticsIds)
    const consent = getAnalyticsConsent()

    if (consent === "rejected") {
      rejectAnalytics(trackingIds)
      return
    }

    if (consent === "accepted" && trackingIds.length > 0) {
      acceptAnalytics(trackingIds)
    }
  }, [trackingIdsKey, googleAnalyticsIds]);

  useEffect(() => {
    if (getAnalyticsConsent() !== "accepted") return
    if (!ReactGA.isInitialized) return
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location.pathname]);

  return null;
}